import { message } from "antd";
import db from "../../../database";
import { verifyStock } from "../../../services";
import messages from "../../../errors/messages.en-IN.json";
import useAdvanceOrderStore from "../../../stores/useAdvanceOrderStore";
import usePOSConfigStore from "../../../stores/usePOSConfigStore";

const sproutz_url = process.env.REACT_APP_SPROUTZ_VERIFYSTOCK;

export const stock = async (props) => {
  const {
    cart,
    setCart,
    tillData,
    setProductStock,
    isFinalValidation,
    setTempCart,
    isCompleteSale,
    setShowCompleteSaleStock,
    setCompleteSaleStockData,
    customMixHandler = false,
    setProductBOMStock = () => {},
    setProductBOMStockData = () => {},
  } = props;
  // BUG-2247: a selectedAddons entry carries only `mAddonProduct.mProductId` (a
  // GUID). verifyStock's `productId` argument expects the product's `value` (SKU) --
  // the same identifier buildCartProducts sends for every normal cart line. Resolve
  // each distinct addon product's SKU from db.products up front, since Dexie is async
  // and consolidateCart is not.
  const addonSkuByProductId = new Map();
  try {
    const pendingAddonProductIds = new Set();
    cart.items.forEach((item) => {
      const addonLines = item.selectedAddons?.length > 0 ? item.selectedAddons : Array.isArray(item.addons) ? item.addons : [];
      addonLines.forEach((addon) => {
        const pid = addon?.mAddonProduct?.mProductId;
        if (pid && !addon.value && !addon?.mAddonProduct?.value) pendingAddonProductIds.add(pid);
      });
    });
    await Promise.all(
      Array.from(pendingAddonProductIds).map(async (pid) => {
        const product = await db.products.where("mProductId").equals(pid).first();
        if (product?.value) addonSkuByProductId.set(pid, product.value);
      })
    );
  } catch (e) {
    console.error("[BUG-2247] addon SKU resolution failed", e);
  }

  const consolidateCart = (cart) => {
    const flatItems = [];

    cart.items.forEach((item) => {
      if (item.productSegment === "CB") {
        if (Array.isArray(item.childItems) && item.childItems.length > 0) {
          item.childItems.forEach((child) => {
            flatItems.push({
              ...child,
              parentLineId: item.lineId,
              type: child?.type,
              weight: child.weight,
              mBatchId: child.mBatchId || null,
              csBunitId: item.csBunitId || null,
              cwrPcatalogueId: item.cwrPcatalogueId || null,
            });
          });
        }
      } else if ((Array.isArray(item.selectedAddons) && item.selectedAddons.length > 0) || (Array.isArray(item.addons) && item.addons.length > 0)) {
        // BUG-2247: a live cart line's chosen add-ons/BOM lines live under `selectedAddons`
        // (stamped by useProductAddons.js -- addDefinedProductWithAddons / handleAddOnValue),
        // never `addons`. `addons` only appears after print-normalization (CWHardwareController)
        // or on an already-persisted order pulled back from history, so on a live cart this
        // branch's old `item.addons` check never matched and the product itself fell through
        // to the `else` below -- getting stock-checked (and, at final validation, capped) against
        // its OWN on-hand qty instead of its components, exactly as the plain else-branch product
        // does. Prefer selectedAddons; fall back to addons for the history/reprint shape.
        // Each selectedAddons entry is the raw productAddons link record (see
        // services/syncQueries.js's productAddons selection) plus a merged `weight` -- it has no
        // `value`/`productId` of its own, only `mAddonProduct.mProductId`. verifyStock keys on the
        // product's `value` (SKU), so use the SKU resolved from db.products above; fall back to the
        // GUID only when it can't be resolved (a no-match there simply skips the check rather than
        // false-blocking the sale).
        const addonLines = item.selectedAddons?.length > 0 ? item.selectedAddons : item.addons;
        const isLiveSelectedAddons = item.selectedAddons?.length > 0;
        addonLines.forEach((addon) => {
          const addonProductId =
            addon?.value ||
            addon?.mAddonProduct?.value ||
            addonSkuByProductId.get(addon?.mAddonProduct?.mProductId) ||
            addon?.productId ||
            addon?.mAddonProduct?.mProductId ||
            null;
          // Mirror useOrderProcessing.js's own selectedAddons -> productAddons qty formula
          // (weight * addonQty) exactly, so the pre-check validates the same quantity that
          // order save actually deducts -- addon.weight defaults to 1 there (an addon line
          // always represents at least 1 unit), not 0, and addonQty covers allowQtySelection.
          const requiredQty = isLiveSelectedAddons ? (Number(addon.weight) || 1) * (Number(addon.addonQty) || 1) : (addon.weight ?? addon.qty ?? 0);
          flatItems.push({
            ...addon,
            value: addonProductId,
            productId: addonProductId,
            parentLineId: item.lineId,
            type: "I",
            weight: requiredQty,
            mBatchId: addon.mBatchId || null,
            csBunitId: item.csBunitId || null,
            cwrPcatalogueId: item.cwrPcatalogueId || null,
          });
        });
      } else if (Array.isArray(item.children) && item.children.length > 0) {
        item.children.forEach((child) => {
          flatItems.push({ ...child, parentLineId: item.lineId }); // Track parent
        });
      } else {
        flatItems.push(item);
      }
    });
    const consolidatedItems = flatItems.map((item) => ({
      ...item,
      weight: Number(item.weight || 0),
    }));
    return { ...cart, items: consolidatedItems };
  };
  const getProductNameById = (productId) => {
    for (const item of cart.items) {
      if (item.value === productId) {
        return item.name || "";
      }

      if (Array.isArray(item.children)) {
        for (const child of item.children) {
          if (child.value === productId) {
            return child.name || " ";
          }
        }
      }

      if (Array.isArray(item.childItems)) {
        for (const child of item.childItems) {
          if (child.value === productId) {
            return child.name || " ";
          }
        }
      }

      const addonLines = Array.isArray(item.selectedAddons) ? item.selectedAddons : Array.isArray(item.addons) ? item.addons : [];
      for (const addon of addonLines) {
        if (
          addon.value === productId ||
          addon?.mAddonProduct?.value === productId ||
          addon?.mAddonProduct?.mProductId === productId ||
          addonSkuByProductId.get(addon?.mAddonProduct?.mProductId) === productId
        )
          return addon.name || "";
      }
    }
    return " ";
  };

  // BUG-2405: verifyStock's billOfMaterials carries only the component SKU (`value`) and
  // qtyOnHand -- resolve each component's name from db.products so the stock drawer can
  // show which component is short, not just a code.
  const withBomNames = async (details) => {
    try {
      const nameBySku = new Map();
      const skus = new Set();
      details.forEach((d) => (d.billOfMaterials || []).forEach((bom) => bom?.value && skus.add(bom.value)));
      await Promise.all(
        Array.from(skus).map(async (sku) => {
          const product = await db.products.where("value").equals(sku).first();
          if (product?.name) nameBySku.set(sku, product.name);
        })
      );
      return details.map((d) =>
        d.billOfMaterials?.length > 0 ? { ...d, billOfMaterials: d.billOfMaterials.map((bom) => ({ ...bom, name: bom.name || nameBySku.get(bom.value) || "" })) } : d
      );
    } catch (e) {
      console.error("[BUG-2405] BOM component name resolution failed", e);
      return details;
    }
  };

  const cleanedCart = consolidateCart(cart);
  const consolidatedCartItems = new Map();

  cleanedCart.items.forEach((item) => {
    const { value, weight, mBatchId, csBunitId, cwrPcatalogueId, alternateUom, uom, uomData, mAddonProduct } = item;

    // TSK-4128: an addon product's configured Alternate Qty is applied server-side via its
    // csUomId (see services/syncQueries.js's productAddons.mAddonProduct selection). Only addon
    // lines carry mAddonProduct on the flattened item, so regular cart lines never get this field.
    const csUomId = mAddonProduct?.csUomId || null;

    let calculatedQty = Number(weight || 0);

    const selectedAlternateUom = Array.isArray(alternateUom) ? alternateUom.find((altUom) => altUom?.csUomId && uom && altUom.csUomId === uom) : null;

    if (selectedAlternateUom) {
      const conversionRate = Number(selectedAlternateUom?.convertionRate ?? 1);

      const matchedUomData = Array.isArray(uomData) ? uomData.find((uomItem) => uomItem?.csUomId === uom) : null;

      // const stdprecision = Number(matchedUomData?.stdprecision ?? 3);

      calculatedQty = Number(weight || 0) * (Number.isNaN(conversionRate) ? 1 : conversionRate);

      calculatedQty = Number(calculatedQty.toFixed(3));
    }

    const uniqueKey = `${value}_${mBatchId}`;

    if (consolidatedCartItems.has(uniqueKey)) {
      const existingItem = consolidatedCartItems.get(uniqueKey);

      existingItem.orderQty = Number(existingItem.orderQty || 0) + calculatedQty;

      consolidatedCartItems.set(uniqueKey, existingItem);
    } else {
      consolidatedCartItems.set(uniqueKey, {
        productId: value,
        mBatchId,
        orderQty: calculatedQty,
        csBunitId,
        cwrPcatalogueId,
        csUomId,
      });
    }
  });

  const buildCartProducts = Array.from(consolidatedCartItems.values())
    .map(({ productId, orderQty, mBatchId, csBunitId, cwrPcatalogueId, csUomId }) => {
      const csUomIdField = csUomId ? `, csUomId: "${csUomId}"` : "";
      if (sproutz_url === "true") {
        return `{ productId: "${productId}",,mBatchId: ${mBatchId ? `"${mBatchId}"` : null}, orderQty: ${orderQty}, cSBunitID: ${
          csBunitId ? `"${csBunitId}"` : null
        }, catalogueId: ${cwrPcatalogueId ? `"${cwrPcatalogueId}"` : null}${csUomIdField} }`;
      } else {
        return `{ productId: "${productId}",,mBatchId: ${mBatchId ? `"${mBatchId}"` : null}, orderQty: ${orderQty}, cSBunitID: ${csBunitId ? `"${csBunitId}"` : null}${csUomIdField} }`;
      }
    })
    .join(", ");
  const data = {
    query:
      sproutz_url === "true"
        ? `
        query { 
          verifyStock1(order: {
            orderNo: "${cart.documentno}"
            tillId: "${tillData.tillAccess.cwrTill.searchKey}"
            cartItems: [${buildCartProducts}]
          }) {
            orderNo
            tillId
            cartItems {
              productId
              orderQty
              mBatchId
              availabilityStatus
              availableQty
              billOfMaterials{
                value
                qtyOnHand
              }
            }
          }
        }
      `
        : `
        query { 
          verifyStock(order: {
            orderNo: "${cart.documentno}"
            tillId: "${tillData.tillAccess.cwrTill.searchKey}"
            cartItems: [${buildCartProducts}]
          }) {
            orderNo
            tillId
            cartItems {
              productId
              orderQty
              mBatchId
              availabilityStatus
              availableQty
              billOfMaterials{
                value
                qtyOnHand
              }
            }
          }
        }
      `,
  };
  try {
    const isAdvanceOrderEnabled = useAdvanceOrderStore.getState().isAdvanceOrderEnabled;
    if (isAdvanceOrderEnabled) {
      return true;
    }
    const response = await verifyStock(data);
    if (response?.status === 200) {
      const { cartItems } = sproutz_url === "true" ? response.data.data.verifyStock1 : response.data.data.verifyStock;
      const posConfig = usePOSConfigStore.getState().posConfig || JSON.parse(localStorage.getItem("posConfig"));
      let totalFlag = true;
      const updateCartItems = (item) => {
        const resItem = cartItems.find((resItem) => resItem.productId === item.value && (item.mBatchId ? resItem.mBatchId === item.mBatchId : true));
        if (!resItem) return item;
        let cappedWeight = item.weight;
        if (resItem.availabilityStatus !== "Yes" && resItem.availableQty < item.weight) {
          totalFlag = false;
          if (resItem.availableQty > 0) {
            cappedWeight = resItem.availableQty;
          }
        } else if (resItem.availabilityStatus !== "Yes") {
          resItem.availableQty -= item.weight;
        }
        const updated = {
          ...item,
          availabilityStatus: resItem.availabilityStatus,
          availableQty: resItem.availableQty,
        };
        if (cappedWeight !== item.weight) {
          const unitPrice = Number(item.sunitprice) || Number(item.salePrice) || 0;
          const taxRate = item.taxRate || 0;
          const taxIncFlag = posConfig?.taxIncludeFlag !== "N";
          const unitTax = taxIncFlag ? unitPrice - unitPrice / (1 + taxRate / 100) : (unitPrice * taxRate) / 100;
          updated.weight = cappedWeight;
          updated.nettotal = taxIncFlag ? cappedWeight * unitPrice : cappedWeight * (unitPrice + unitTax);
          updated.taxAmount = unitTax * cappedWeight;
        }
        return updated;
      };
      if (isFinalValidation) {
        const updatedCartData = cart.items.map(updateCartItems);
        const updatedCartItems = [];

        const groupedByParent = {};

        updatedCartData.forEach((item) => {
          if (item.childrenId) {
            if (!groupedByParent[item.childrenId]) {
              groupedByParent[item.childrenId] = [];
            }
            groupedByParent[item.childrenId].push(item);
          } else {
            updatedCartItems.push(item); // normal item
          }
        });

        // Merge children back into their parent items
        cart.items.forEach((originalItem) => {
          const lineId = originalItem.lineId;
          if (groupedByParent[lineId]) {
            updatedCartItems.push({
              ...originalItem,
              children: groupedByParent[lineId],
            });
          } else if (originalItem.productSegment === "CB" && Array.isArray(originalItem.childItems)) {
            const updatedChildren = originalItem.childItems.map((child) => {
              return updatedCartData.find((item) => item.value === child.value && (child.mBatchId ? item.mBatchId === child.mBatchId : true)) || child;
            });

            updatedCartItems.push({
              ...originalItem,
              childItems: updatedChildren,
            });
          }
        });
        const updatedCart = { ...cart, items: updatedCartData };
        setCart(updatedCart);
        setTempCart(JSON.parse(JSON.stringify(updatedCart)));
        return totalFlag;
      } else {
        const getCalculatedQty = (item) => {
          const { weight, alternateUom, uom, uomData } = item;

          let calculatedQty = Number(weight || 0);

          const selectedAlternateUom = Array.isArray(alternateUom) ? alternateUom.find((altUom) => altUom?.csUomId && uom && altUom.csUomId === uom) : null;

          if (selectedAlternateUom) {
            const conversionRate = Number(selectedAlternateUom?.convertionRate ?? 1);

            const matchedUomData = Array.isArray(uomData) ? uomData.find((uomItem) => uomItem?.csUomId === uom) : null;

            // const stdprecision = Number(matchedUomData?.stdprecision ?? 3);

            calculatedQty = Number(weight || 0) * (Number.isNaN(conversionRate) ? 1 : conversionRate);

            calculatedQty = Number(calculatedQty.toFixed(3));
          }

          return calculatedQty;
        };

        // Collect per-item stock failures instead of just a boolean
        const failedStockItems = [];
        // Tracks qty already claimed by earlier cart lines against the same resItem
        // (e.g. two lines of the same product/batch) WITHOUT mutating resItem.availableQty,
        // which is the raw verifyStock API value re-read later to populate the
        // "Stock is not available" popup (stockDetails below). Mutating it in place used to
        // drive that popup's Available/Shortfall columns negative (BUG-1773).
        const claimedQtyByResItem = new Map();
        cleanedCart.items.forEach((item) => {
          // Detector must match the same items the add-to-cart gate lets through (cart.js: type === "I" || !type).
          // Items with a blank/undefined type were being stock-checked but never flagged here, so a shortage
          // slipped through and the item was added anyway. Service items ("S") stay excluded.
          const resItem = cartItems.find((resItem) => resItem?.productId === item?.value && (item?.type === "I" || item?.type === "FGS" || !item?.type) && (item.mBatchId ? resItem.mBatchId === item.mBatchId : true));
          if (resItem && resItem.availabilityStatus !== "Yes") {
            const calculatedQty = getCalculatedQty(item);
            const alreadyClaimed = claimedQtyByResItem.get(resItem) || 0;
            const remainingAvailableQty = (Number(resItem.availableQty) || 0) - alreadyClaimed;
            if (remainingAvailableQty < calculatedQty) {
              failedStockItems.push({
                productId: item.value || item.productId || item.mProductId,
                name: item.name || getProductNameById(item.value),
                requestedQty: calculatedQty,
                availableQty: remainingAvailableQty,
                mBatchId: item.mBatchId || null,
                // TIC-9337: consolidateCart flattens selectedAddons into plain `type: "I"` items
                // (see the addon branch above), spreading the original addon record onto them --
                // mProductAddOnId/mAddonProduct only survive on addon-origin items, never on a
                // plain cart product. This is the only way to tell the two apart down here.
                isAddon: !!(item.mProductAddOnId || item.mAddonProduct),
              });
            }
            claimedQtyByResItem.set(resItem, alreadyClaimed + calculatedQty);
          }
        });

        const hasInsufficientStock = failedStockItems.length > 0;

        if (hasInsufficientStock && tillData.tillAccess.csBunit.allowNegativeStock !== "Y") {
          if (customMixHandler) {
            // Return detailed failure info for custom mix to show specific product names
            return { passed: false, failedItems: failedStockItems };
          }
          if (isCompleteSale) {
            const stockDetails = await withBomNames(
              cartItems.map((item) => ({
                ...item,
                name: getProductNameById(item.productId),
              }))
            );
            setCompleteSaleStockData(stockDetails);
            setShowCompleteSaleStock(true);
          } else if (
            failedStockItems.every((item) => {
              const resItem = cartItems.find(
                (cartItem) => cartItem.productId === item.productId && (item.mBatchId ? cartItem.mBatchId === item.mBatchId : true)
              );
              return !item.isAddon && (Number(item.availableQty) || 0) <= 0 && !(resItem?.billOfMaterials?.length > 0);
            })
          ) {
            // Every failed item has zero stock AND no BOM data to report — the plain
            // "Stock is not available" popup, not the detail drawer. A zero availableQty with
            // BOM data present still needs the drawer, since that's the AutoBOM case where the
            // shortfall is actually in a component (e.g. billOfMaterials qtyOnHand=0), not the
            // finished product itself. TIC-9337: an addon failure never qualifies for this plain
            // popup, even at zero stock -- it always needs the item name/qty the drawer shows,
            // since there's no other way for staff to tell which of several addons is short.
            setProductBOMStockData([]);
            setProductBOMStock(false);
            setProductStock(true);
          } else {
            // Single shared drawer for both BOM and plain insufficient-stock cases: reattach each
            // failed item's billOfMaterials (present only for BOM/make-to-order products) so the
            // drawer can render the component breakdown when there is one, and hide it when there isn't.
            const stockDetails = failedStockItems.map((failedItem) => {
              const resItem = cartItems.find(
                (item) => item.productId === failedItem.productId && (failedItem.mBatchId ? item.mBatchId === failedItem.mBatchId : true)
              );
              return {
                productId: failedItem.productId,
                name: failedItem.name,
                orderQty: failedItem.requestedQty,
                availableQty: failedItem.availableQty,
                availabilityStatus: resItem?.availabilityStatus,
                billOfMaterials: resItem?.billOfMaterials || [],
              };
            });
            setProductBOMStockData(await withBomNames(stockDetails));
            setProductBOMStock(true);
          }
          return false;
        }
      }
      return true;
    } else {
      message.error(messages.errors.orders.retrieve_stock_error);
      return false;
    }
  } catch (error) {
    console.error("Error checking stock:", error);
    message.error(messages.errors.orders.retrieve_stock_error);
    return false;
  }
};
